/**
 * Billing Tab - Subscription management and billing information
 */

import { AlertTriangle } from '@untitledui/icons';
import { useCallback, useMemo, useRef } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useBilling, usePlans } from '@/hooks/billing/useBilling';
import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';

import {
  CreditsSection,
  DiscountBanner,
  InvoiceHistorySection,
  PaymentMethodSection,
  PlanPickerSection,
  PostTrialExpiredState,
  ResumeInviteCheckoutBanner,
  SubscriptionSection,
  TrialBanner,
} from './billing';

/**
 * Skeleton loader for subscription sections
 */
function BillingSkeletonLoader() {
  return (
    <div
      className='space-y-6 animate-pulse'
      aria-label='Loading billing information'
    >
      {/* Subscription skeleton */}
      <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
        <div className='px-6 py-5 border-b border-border-secondary'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 bg-base-300 rounded-full' />
            <div className='space-y-2'>
              <div className='w-24 h-4 bg-base-300 rounded' />
              <div className='w-40 h-3 bg-base-300 rounded' />
            </div>
          </div>
        </div>
        <div className='p-6'>
          <div className='grid grid-cols-2 gap-6'>
            <div className='space-y-4'>
              <div className='w-16 h-3 bg-base-300 rounded' />
              <div className='w-24 h-4 bg-base-300 rounded' />
            </div>
            <div className='space-y-4'>
              <div className='w-16 h-3 bg-base-300 rounded' />
              <div className='w-20 h-6 bg-base-300 rounded-full' />
            </div>
          </div>
        </div>
      </div>

      {/* Payment method skeleton */}
      <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
        <div className='px-6 py-5 border-b border-border-secondary'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 bg-base-300 rounded-full' />
            <div className='space-y-2'>
              <div className='w-32 h-4 bg-base-300 rounded' />
              <div className='w-48 h-3 bg-base-300 rounded' />
            </div>
          </div>
        </div>
        <div className='p-6'>
          <div className='flex items-center gap-4'>
            <div className='w-12 h-8 bg-base-300 rounded' />
            <div className='space-y-2'>
              <div className='w-20 h-4 bg-base-300 rounded' />
              <div className='w-24 h-3 bg-base-300 rounded' />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Error state with retry option
 */
function BillingErrorState({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) {
  return (
    <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm p-8'>
      <div className='flex flex-col items-center text-center'>
        <FeaturedIcon
          icon={AlertTriangle}
          color='error'
          theme='modern'
          size='lg'
          className='mb-4'
        />
        <h3 className='text-lg font-semibold text-primary mb-2'>
          Unable to Load Billing
        </h3>
        <p className='text-sm text-tertiary mb-6 max-w-md'>{error}</p>
        <Button color='secondary' size='sm' onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
}

/**
 * Cancellation warning banner
 */
function CancellationWarning({
  endDate,
  onReactivate,
  isLoading,
}: {
  endDate: string;
  onReactivate: () => void;
  isLoading?: boolean;
}) {
  const formattedDate = new Date(endDate).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className='relative flex flex-col gap-4 rounded-xl border border-primary bg-primary_alt p-4 shadow-xs md:flex-row md:items-center'>
      <FeaturedIcon
        icon={AlertTriangle}
        color='warning'
        theme='light-outline'
        size='md'
      />
      <div className='flex flex-1 flex-col gap-1 md:w-0'>
        <p className='text-sm font-semibold text-secondary'>
          Your subscription will end on {formattedDate}
        </p>
        <p className='text-sm text-tertiary'>
          You&apos;ll lose access to premium features after this date.
        </p>
      </div>
      <Button
        color='primary'
        size='sm'
        onClick={onReactivate}
        isLoading={isLoading}
        isDisabled={isLoading}
      >
        Reactivate
      </Button>
    </div>
  );
}

export function BillingTab() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id ?? 0;
  const planPickerRef = useRef<HTMLDivElement>(null);

  const {
    subscription,
    loading,
    error,
    hasSubscription,
    portalLoading,
    portalError,
    checkoutLoading,
    seatChangeLoading,
    seatChangeError,
    cadenceChangeLoading,
    cadenceChangeError,
    retry,
    openPortal,
    openCheckout,
    changeSeats,
    changeCadenceToYearly,
    resumeInviteCheckout,
    resumeInviteLoading,
    resumeInviteError,
  } = useBilling(orgId);

  const {
    plans,
    loading: plansLoading,
    error: plansError,
    reload: reloadPlans,
  } = usePlans(orgId);

  // Look up display copy for the active tier. The /plans/ endpoint is the
  // source for tier names/descriptions; `subscription.billing_tier` is the
  // slug that joins back to it.
  const activeTierName = useMemo(() => {
    if (!subscription?.billing_tier) {
      return undefined;
    }
    return plans.find(p => p.tier === subscription.billing_tier)?.name;
  }, [plans, subscription?.billing_tier]);

  // PostTrialExpiredState's CTA scrolls the user to the plan-picker — when
  // the trial has expired we show BOTH the empty state and the picker so
  // they can re-subscribe without leaving the page.
  const handleChoosePlan = useCallback(() => {
    planPickerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const handleRetryPlans = useCallback(() => {
    reloadPlans();
  }, [reloadPlans]);

  // Loading state — wait for both calls so we can pick the right state
  // (subscribed vs picker) without flicker.
  if (loading || plansLoading) {
    return <BillingSkeletonLoader />;
  }

  // Error state — subscription error takes precedence (it's the primary UI).
  if (error) {
    return <BillingErrorState error={error} onRetry={retry} />;
  }

  const subscriptionInfo = subscription?.subscription;
  // ``incomplete_expired`` is Stripe's terminal state when a trial ends
  // without a payment method on file. The customer still has a
  // Subscription row (so hasSubscription is true via has_active_subscription
  // logic) but it can't be reactivated — they need to pick a plan again.
  const isPostTrialExpired = subscriptionInfo?.status === 'incomplete_expired';

  // Paid-invite resume state — the org was created via a paid invite code
  // but the holder closed out of their initial Stripe Checkout tab. We
  // surface a dedicated "Resume checkout" banner instead of the catalog
  // PlanPicker so the customer ends up on the invite's deal terms
  // (negotiated price, coupon, trial), not whatever self-serve they would
  // pick from the list.
  const pendingInviteCheckout =
    !hasSubscription && subscription?.pending_invite_checkout === true;

  if (pendingInviteCheckout) {
    return (
      <div className='space-y-6'>
        <ResumeInviteCheckoutBanner
          onResume={resumeInviteCheckout}
          isLoading={resumeInviteLoading}
          errorMessage={resumeInviteError}
        />
      </div>
    );
  }

  // No subscription state — show the plan picker so the user can subscribe.
  if (!hasSubscription || !subscription || isPostTrialExpired) {
    return (
      <div className='space-y-6'>
        {isPostTrialExpired && (
          <PostTrialExpiredState onChoosePlan={handleChoosePlan} />
        )}
        {plansError && (
          <div className='flex flex-col gap-3 rounded-xl border border-border-secondary bg-bg-primary p-4 text-sm sm:flex-row sm:items-center sm:justify-between'>
            <p className='text-[var(--color-text-error-primary)]'>
              {plansError}
            </p>
            <Button
              color='secondary'
              size='sm'
              onClick={handleRetryPlans}
              isLoading={plansLoading}
              isDisabled={plansLoading}
            >
              Try again
            </Button>
          </div>
        )}
        {plans.length > 0 && (
          <div ref={planPickerRef}>
            <PlanPickerSection
              plans={plans}
              onSubscribe={openCheckout}
              isLoading={checkoutLoading}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Cancellation warning if applicable. Mutually exclusive with the
          trial banner — Stripe cancellations don't apply to trialing subs. */}
      {subscriptionInfo?.cancel_at_period_end && (
        <CancellationWarning
          endDate={subscriptionInfo.current_period_end}
          onReactivate={openPortal}
          isLoading={portalLoading}
        />
      )}

      {/* Trial banner — only when not cancelling, only while trialing.
          ``hasPaymentMethod`` flips the banner from "Add payment method"
          urgency to a friendly "we'll charge your card" heads-up. Stripe
          Checkout collects the card up front for trial subscriptions, so
          the common paid-invite trial flow lands here with
          payment_method != null. */}
      {!subscriptionInfo?.cancel_at_period_end &&
        subscriptionInfo?.is_trialing &&
        subscriptionInfo?.trial_end && (
          <TrialBanner
            trialEnd={subscriptionInfo.trial_end}
            daysRemaining={subscriptionInfo.trial_days_remaining ?? 0}
            hasPaymentMethod={subscription.payment_method !== null}
            onAddPaymentMethod={openPortal}
            isPortalLoading={portalLoading}
          />
        )}

      {/* Discount banner — orthogonal to trial and cancellation. */}
      {subscriptionInfo?.discount && (
        <DiscountBanner discount={subscriptionInfo.discount} />
      )}

      {/* Subscription info */}
      <SubscriptionSection
        subscription={subscriptionInfo ?? null}
        tierName={activeTierName}
        currentTier={subscription.billing_tier || undefined}
        currentCadence={subscription.billing_cadence || undefined}
        plans={plans}
        purchasedSeats={subscription.purchased_seats}
        perSeatCents={subscription.per_seat_cents}
        pendingSeatCount={subscription.pending_seat_count}
        pendingSeatChangeAt={subscription.pending_seat_change_at}
        canManageBilling={subscription.is_admin}
        onManageBilling={openPortal}
        onChangeSeats={changeSeats}
        onSwitchToYearly={changeCadenceToYearly}
        portalLoading={portalLoading}
        portalError={portalError}
        seatChangeLoading={seatChangeLoading}
        seatChangeError={seatChangeError}
        cadenceChangeLoading={cadenceChangeLoading}
        cadenceChangeError={cadenceChangeError}
      />

      {/* Payment method */}
      <PaymentMethodSection
        paymentMethod={subscription.payment_method}
        billingAddress={subscription.billing_address}
        canManageBilling={subscription.is_admin}
        onUpdatePaymentMethod={openPortal}
        portalLoading={portalLoading}
      />

      {/* credits */}
      <CreditsSection orgId={orgId} />

      {/* Invoice history */}
      <InvoiceHistorySection orgId={orgId} />
    </div>
  );
}
